import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDiary } from '@/diary/DiaryProvider'
import { Ionicon } from '@/components/ui/Ionicon'
import { PageSheet } from '@/components/log/PageSheet'
import { ManualEntryForm } from '@/components/log/ManualEntryForm'
import { getMealTypeForTime } from '@/lib/mealTime'
import type { MealTime } from '@/types'

/**
 * Manual Entry sheet — web port of mobile components/diary/ManualEntrySheet.tsx
 * (pageSheet): "Manual Entry" / "to {meal}" header with the round close
 * button, then the form. The entry lands on the diary's selected date.
 */
export function ManualEntryModal({
  open,
  onClose,
  defaultMeal,
}: {
  open: boolean
  onClose: () => void
  /** The diary section it was opened for; none = the meal for the time it opened (mobile). */
  defaultMeal?: MealTime | null
}) {
  const { t } = useTranslation()
  const { addEntry } = useDiary()
  const [timeMeal, setTimeMeal] = useState<MealTime>(() => getMealTypeForTime())
  useEffect(() => {
    if (open) setTimeMeal(getMealTypeForTime())
  }, [open])
  const meal = defaultMeal ?? timeMeal

  return (
    <PageSheet
      open={open}
      onClose={onClose}
      label={t('diary.manualEntry.title', 'Manual Entry')}
      header={
        <div className="flex items-start justify-between px-lg pb-md pt-lg">
          <div className="me-md min-w-0 flex-1">
            <h2 className="text-[20px] font-bold leading-[normal] tracking-[-0.3px] text-text">
              {t('diary.manualEntry.title', 'Manual Entry')}
            </h2>
            <p className="mt-[2px] text-[13px] leading-[normal] text-text-secondary">
              {t('diary.manualEntry.subtitle', { meal: t(`diary.meals.${meal}`) })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-border/50 text-text-secondary"
          >
            <Ionicon name="close" size={20} />
          </button>
        </div>
      }
    >
      <div className="px-lg pb-xl">
        {open ? <ManualEntryForm key={defaultMeal ?? 'none'} meal={meal} onAdd={addEntry} onDone={onClose} /> : null}
      </div>
    </PageSheet>
  )
}
